import { useCallback, useEffect, useRef, useState } from 'react';
import {
  approveDocument,
  cancelSignRequests,
  dictateAgreement,
  createDocument,
  getRomEditorState,
  sendRomToParties,
  updateStipulationTerms,
  uploadExternalDocument,
  shareDocumentWithCourt,
  unshareDocumentWithCourt,
  voidDocument,
  EngineError,
  getDocumentHtml,
  getDocumentPdfUrl,
  getSigningStatus,
  listCaseDocuments,
  sendSignRequests,
  updateRomDocument,
  type RomEdits,
} from '../../api/engineDashboard';
import { docTypeLabel, formatDateTime } from '../../lib/labels';
import { confirmDialog } from '../../lib/confirmDialog';
import type { DocType, DocumentRecord, SigningStatus } from '../../types/dashboard';

interface Props {
  token: string;
  caseId: string;
  canMutate: boolean;
  /** Opens the full-page stipulation editor (Mo's Loom, 2026-09-15). */
  onOpenStipEditor?: (agreementId: string) => void;
  /** Seed for the ROM participant editor: the case's parties with their
      printed role labels (never the mediator). */
  romSeedParticipants?: Array<{ name: string; role_label: string }>;
  /** Drafting is gated until a mediation has actually happened — before
      that the AI has only intake data and produces hollow documents.
      True once a booked session's start time has passed or the case has
      reached MEDIATING/RESOLVED/CLOSED. */
  mediationHeld?: boolean;
  /** rent_delinquency cases draft the Door resolution agreement (a
      deterministic template) instead of the court stipulation. */
  domainPack?: string;
  onUnauthorized: () => void;
}

/**
 * Case documents: 1-click Stipulation / Report of Mediation drafts with
 * per-signer signing status (sign slugs come back from the generate call).
 *
 * The Engine's document endpoints predate mediator JWTs and may still be
 * org-key-only; every call degrades to a soft notice instead of breaking
 * the case view.
 */
export function DocumentsPanel({ token, caseId, canMutate, domainPack, romSeedParticipants, onUnauthorized, onOpenStipEditor }: Props) {
  const [documents, setDocuments] = useState<DocumentRecord[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [working, setWorking] = useState<DocType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState<Record<string, SigningStatus>>({});

  const load = useCallback(() => {
    listCaseDocuments(token, caseId)
      .then((res) => {
        setDocuments(res.documents);
        setUnavailable(false);
      })
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) {
          onUnauthorized();
          return;
        }
        // 403/500 = endpoint not yet open to mediator sessions.
        setDocuments([]);
        setUnavailable(true);
      });
  }, [token, caseId, onUnauthorized]);

  useEffect(load, [load]);

  // In-app viewer: the Zoom webview blocks window.open AND won't render
  // blob: iframes — so the modal uses a bare same-origin iframe and writes
  // the document into it directly. No URL scheme, nothing to block.
  const [viewerHtml, setViewerHtml] = useState<string | null>(null);
  const closeViewer = () => setViewerHtml(null);

  const mountViewerFrame = (el: HTMLIFrameElement | null) => {
    if (!el || !viewerHtml || el.dataset.written === '1') return;
    const frameDoc = el.contentDocument;
    if (!frameDoc) return;
    frameDoc.open();
    frameDoc.write(viewerHtml);
    frameDoc.close();
    el.dataset.written = '1';
  };

  const openPdf = async (agreementId: string) => {
    setError(null);
    try {
      const blobUrl = await getDocumentPdfUrl(token, agreementId);
      window.open(blobUrl, '_blank', 'noopener');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not open the PDF.');
    }
  };

  const viewDoc = async (agreementId: string) => {
    setError(null);
    try {
      setViewerHtml(await getDocumentHtml(token, agreementId));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError('Could not load the document.');
    }
  };

  const sendRequests = async (agreementId: string) => {
    if (!(await confirmDialog('Email/text each pending signer their private signing link?', { danger: true, confirmLabel: 'Send links' }))) return;
    setError(null);
    try {
      await sendSignRequests(token, agreementId);
      void checkSigning(agreementId);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not send signing links.');
    }
  };

  const [copiedSig, setCopiedSig] = useState<string | null>(null);
  const copySignLink = async (sigId: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copy the signing link:', url);
    }
    setCopiedSig(sigId);
    setTimeout(() => setCopiedSig((prev) => (prev === sigId ? null : prev)), 2000);
  };

  // Dictation (Danny ticket, 2026-09-15): manual drafting made consistent
  // with the Zoom flow — the mediator recites the resolution, the engine
  // transcribes it and Opus drafts the same reviewable document.
  const [dictState, setDictState] = useState<'idle' | 'recording' | 'drafting'>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const toggleDictation = async () => {
    if (dictState === 'recording') {
      recorderRef.current?.stop();
      return;
    }
    setError(null);
    // The Zoom client's built-in browser exposes no microphone API at
    // all (same family as its window.confirm block) — detect that first
    // so the message says what to actually do instead of a generic
    // permission complaint (Danny, 2026-09-16: "not even an option to
    // turn it on").
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This window cannot record audio — the Zoom app\'s built-in browser blocks the microphone. Open dash.belevelheaded.com in Chrome or Safari to dictate, or use ✍️ Describe terms to type it instead.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as DOMException)?.name ?? '';
      setError(
        name === 'NotFoundError'
          ? 'No microphone was found on this device. Use ✍️ Describe terms to type the resolution instead.'
          : name === 'NotAllowedError'
          ? 'Microphone access is blocked. Click the camera/mic icon in the address bar to allow it (on a Mac, also check System Settings → Privacy → Microphone for your browser), then try again — or use ✍️ Describe terms.'
          : 'Could not start recording (' + (name || 'unknown error') + '). Try ✍️ Describe terms to type the resolution instead.',
      );
      return;
    }
    const rec = new MediaRecorder(stream);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const audio = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      if (audio.size < 2000) { setDictState('idle'); setError('That recording was empty — try again.'); return; }
      setDictState('drafting');
      try {
        const res = await dictateAgreement(token, caseId, audio);
        load();
        // Straight into review: the drafted terms open in the editor.
        setTimeout(() => void openStipEditor(res.agreement_id), 400);
      } catch (err) {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setError(err instanceof EngineError ? err.message : 'Dictation drafting failed — try again or enter terms manually.');
      } finally {
        setDictState('idle');
      }
    };
    rec.start();
    recorderRef.current = rec;
    setDictState('recording');
  };

  const draft = async (docType: DocType) => {
    setWorking(docType);
    setError(null);
    try {
      const res = await createDocument(token, caseId, docType);
      // Regeneration returns the SAME row — replace, never duplicate
      // (repeat Draft presses stacked phantom rows — found live).
      setDocuments((prev) => {
        const rest = (prev ?? []).filter((d) => d.id !== res.agreement.id);
        return [res.agreement, ...rest];
      });
      // The full-page editor is THE stipulation experience (Mo's Loom,
      // 2026-09-15) — drafting one lands the mediator straight in it.
      if (['stipulation', 'resolution_agreement', 'certificate_of_attempts'].includes(docType) && onOpenStipEditor) onOpenStipEditor(res.agreement.id);
      setUnavailable(false);
      // The generate response already carries signer slugs/urls.
      setSigning((prev) => ({
        ...prev,
        [res.agreement.id]: {
          agreement_id: res.agreement.id,
          total: res.signatures.length,
          signed: 0,
          declined: 0,
          pending: res.signatures.length,
          all_signed: false,
          signers: res.signatures,
        },
      }));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(
        err instanceof EngineError && err.status === 403
          ? 'Document drafting is not enabled for your session yet.'
          : err instanceof Error
            ? err.message
            : 'Could not draft the document',
      );
    } finally {
      setWorking(null);
    }
  };

  // Off-platform documents (Danny, 2026-09-14): a stip signed on paper or
  // a ROM made elsewhere is uploaded as a PDF and BECOMES the document of
  // that type; clerks see it only after the explicit court share.
  const uploadRef = { current: null as HTMLInputElement | null };
  const [uploadType, setUploadType] = useState<'stipulation' | 'rom'>('stipulation');
  const [sharing, setSharing] = useState<string | null>(null);
  const pickUpload = (docType: 'stipulation' | 'rom') => {
    setUploadType(docType);
    uploadRef.current?.click();
  };
  const onUploadFile = async (file: File | null) => {
    if (!file) return;
    setWorking(uploadType);
    setError(null);
    try {
      await uploadExternalDocument(token, caseId, uploadType, file);
      load();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Upload failed.');
    } finally {
      setWorking(null);
    }
  };
  const shareWithCourt = async (agreementId: string) => {
    if (!(await confirmDialog('Share this document with the court? Clerks and judges will see it in their documents view immediately.', { danger: true, confirmLabel: 'Share with court' }))) return;
    setSharing(agreementId);
    setError(null);
    try {
      await shareDocumentWithCourt(token, agreementId);
      load();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not share with the court.');
    } finally {
      setSharing(null);
    }
  };

  // ROM review editor: participants as printed + optional notes.
  const [romEditing, setRomEditing] = useState<string | null>(null);
  const [romRows, setRomRows] = useState<Array<{ name: string; role_label: string }>>([]);
  const [romNotes, setRomNotes] = useState('');
  const [romReportText, setRomReportText] = useState('');
  const [romBusy, setRomBusy] = useState(false);
  const [romSavedAt, setRomSavedAt] = useState<string | null>(null);

  // The editor opens with the report's CURRENT content (what actually
  // prints) — participants, report text, notes — never blank fields.
  const openRomEditor = async (agreementId: string) => {
    setRomEditing(agreementId);
    setRomRows([{ name: 'Loading…', role_label: '' }]);
    setRomNotes('');
    setRomReportText('');
    try {
      const state = await getRomEditorState(token, agreementId);
      setRomRows(state.participants.length ? state.participants : romSeedParticipants ?? [{ name: '', role_label: '' }]);
      setRomReportText(state.report_text);
      setRomNotes(state.notes);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setRomRows(romSeedParticipants?.length ? romSeedParticipants.map((p) => ({ ...p })) : [{ name: '', role_label: '' }]);
      setError('Could not load the current report content.');
    }
  };

  const sendRom = async (agreementId: string) => {
    if (!(await confirmDialog('Send the reviewed Report of Mediation to ALL parties (SMS/email)?', { danger: true, confirmLabel: 'Send to parties' }))) return;
    setRomBusy(true);
    setError(null);
    try {
      const res = await sendRomToParties(token, agreementId);
      setDocuments((prev) => (prev ?? []).map((d) => (d.id === agreementId ? { ...d, status: 'sent' } : d)));
      window.alert(`Report sent: ${res.queued} message(s) queued.`);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not send the report.');
    } finally {
      setRomBusy(false);
    }
  };

  const saveRom = async (agreementId: string, approve: boolean) => {
    setRomBusy(true);
    setError(null);
    try {
      const rom: RomEdits = {
        participants: romRows.filter((r) => r.name.trim() && r.name !== 'Loading…'),
        notes: romNotes.trim() || null,
        report_text: romReportText.trim() || null,
      };
      await updateRomDocument(token, agreementId, rom);
      if (approve) {
        await approveDocument(token, agreementId);
        setDocuments((prev) =>
          (prev ?? []).map((d) => (d.id === agreementId ? { ...d, status: 'approved' } : d)),
        );
        setRomEditing(null);
      } else {
        // Keep the editor open with visible confirmation — a silent close
        // read as "save did nothing" and led to destructive re-drafts.
        setRomSavedAt(agreementId);
        setTimeout(() => setRomSavedAt(null), 4000);
      }
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not save the report.');
    } finally {
      setRomBusy(false);
    }
  };

  // Stipulation terms editor (spec 5.4) — one term per line.
  const [stipEditing, setStipEditing] = useState<string | null>(null);
  // COA cancel flow (Danny, 2026-09-21): inline reason picker, no browser dialogs.
  const [coaCancelling, setCoaCancelling] = useState<string | null>(null);
  // Draft stip/resolution cancel (Scott, 2026-09-21): plain two-press.
  const [docCancelling, setDocCancelling] = useState<string | null>(null);
  const [coaReason, setCoaReason] = useState<'engaged' | 'other'>('engaged');
  const [coaOtherText, setCoaOtherText] = useState('');
  const [stipTerms, setStipTerms] = useState('');
  const [stipBusy, setStipBusy] = useState(false);

  const openStipEditor = async (agreementId: string) => {
    setStipEditing(agreementId);
    setStipTerms('Loading…');
    try {
      const html = await getDocumentHtml(token, agreementId);
      // Pull the current terms out of the rendered list for editing.
      const matches = [...html.matchAll(/<li>(.*?)<\/li>/gs)]
        .map((m) => m[1].replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#39;/g, "'").replace(/&quot;/g, '"').trim())
        .filter(Boolean);
      setStipTerms(matches.join('\n'));
    } catch {
      setStipTerms('');
    }
  };

  const saveStip = async (agreementId: string) => {
    const terms = stipTerms.split('\n').map((t) => t.trim()).filter((t) => t && t !== 'Loading…');
    if (!terms.length) return;
    setStipBusy(true);
    setError(null);
    try {
      await updateStipulationTerms(token, agreementId, terms);
      setStipEditing(null);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not save the stipulation.');
    } finally {
      setStipBusy(false);
    }
  };

  // Void & re-draft (spec 5.3): kill the current document, generate a
  // fresh one; "Send signing links" is the re-execute step.
  const voidAndRedraft = async (doc: DocumentRecord) => {
    if (!(await confirmDialog(`Void this ${docTypeLabel(doc.doc_type)}${doc.status === 'signed' ? ' (ALREADY SIGNED)' : ''} and draft a replacement? Existing signing links stop working immediately.`, { danger: true, confirmLabel: 'Void & redraft' }))) return;
    setError(null);
    try {
      await voidDocument(token, doc.id);
      setDocuments((prev) => (prev ?? []).map((d) => (d.id === doc.id ? { ...d, status: 'voided' } : d)));
      await draft(doc.doc_type as DocType);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not void the document.');
    }
  };

  const checkSigning = async (agreementId: string) => {
    try {
      const status = await getSigningStatus(token, agreementId);
      setSigning((prev) => ({ ...prev, [agreementId]: status }));
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError('Could not load signing status');
    }
  };

  return (
    <section className="card p-4">
      {viewerHtml && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/60 p-4 sm:p-8">
          <div className="flex justify-end pb-2">
            <button type="button" className="btn-secondary" onClick={closeViewer}>
              Close
            </button>
          </div>
          <iframe
            title="Document"
            ref={mountViewerFrame}
            className="min-h-0 w-full flex-1 rounded-lg border border-surface-border bg-white"
          />
        </div>
      )}
      <div className="section-label">Documents</div>

      {canMutate && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {domainPack === 'rent_delinquency' ? (
              <button
                type="button"
                className="btn-primary !text-xs"
                disabled={working !== null}
                title="Deterministic Door template — sent to the resident and the property manager for signature"
                onClick={() => void draft('resolution_agreement')}
              >
                {working === 'resolution_agreement' ? 'Drafting…' : 'Draft Resolution Agreement'}
              </button>
            ) : (
            <button
              type="button"
              className="btn-primary !text-xs"
              disabled={working !== null}
              title="Draft a stipulation at any time; terms come from the mediated agreement when one exists, or start empty for you to fill in."
              onClick={() => void draft('stipulation')}
            >
              {working === 'stipulation' ? 'Drafting…' : 'Draft Stipulation'}
            </button>
            )}
            <button
              type="button"
              className="btn-primary !text-xs"
              disabled={working !== null}
              title="Drafts from whatever the case shows so far (held sessions, outreach attempts, outcome). No Zoom session required; review and edit before sending."
              onClick={() => void draft('rom')}
            >
              {working === 'rom' ? 'Drafting…' : 'Draft ROM'}
            </button>
            {domainPack === 'court_dispute' && (
              <button
                type="button"
                className="btn-secondary !text-xs"
                disabled={working !== null}
                title="Certificate of Attempts: documents who engaged and every attempt to reach the non-responding party. Auto-drafts from the outreach log; open it afterward to add attempts made off-system."
                onClick={() => void draft('certificate_of_attempts')}
              >
                {working === 'certificate_of_attempts' ? 'Drafting…' : 'Draft COA'}
              </button>
            )}
            <button
              type="button"
              className={dictState === 'recording' ? 'btn-primary !bg-red-600 !text-xs' : 'btn-secondary !text-xs'}
              disabled={working !== null || dictState === 'drafting'}
              title="Same experience as drafting in Zoom, without the session: recite the full resolution aloud (parties, amounts, dates, method of payment), stop, and review the drafted document before sending for signatures."
              onClick={() => void toggleDictation()}
            >
              {dictState === 'recording'
                ? '⏺ Recording — click to finish'
                : dictState === 'drafting'
                ? 'Transcribing & drafting…'
                : '🎤 Dictate terms…'}
            </button>
          </div>
          {dictState === 'recording' && (
            <p className="mt-1 text-[11px] text-red-600">
              Recording. Recite the resolution as you would summarize it to the parties — names, amounts, dates,
              payment method, and what happens on default — then click to finish.
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
            <span>Signed outside the platform?</span>
            <button type="button" className="text-brand underline decoration-dotted hover:decoration-solid" onClick={() => pickUpload('stipulation')}>
              Upload Stipulation PDF…
            </button>
            <button type="button" className="text-brand underline decoration-dotted hover:decoration-solid" onClick={() => pickUpload('rom')}>
              Upload ROM PDF…
            </button>
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              ref={(el) => { uploadRef.current = el; }}
              onChange={(e) => {
                void onUploadFile(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />
          </div>

        </>
      )}

      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}

      {unavailable && (documents?.length ?? 0) === 0 && (
        <p className="mt-2 text-xs text-gray-500">
          Existing documents aren&rsquo;t visible to your session yet.
        </p>
      )}

      {documents && documents.length > 0 && (
        <ul className="mt-2 space-y-2">
          {documents.map((doc) => {
            const status = signing[doc.id];
            // Only stipulations / resolution agreements are party-signed.
            // The ROM and the Certificate of Attempts are Levelheaded's own
            // attestations — no signing controls (Rachel, 2026-09-04).
            const isSignable = (doc.doc_type === 'stipulation' || doc.doc_type === 'resolution_agreement') && doc.external_upload !== true;
            const isCertificate = doc.doc_type === 'certificate_of_attempts';
            // Done = green, unmissably (Danny, 2026-09-08): a signable doc
            // is done when every signer signed; the ROM is done once
            // finalized (approved/sent) — visible to the court from then.
            const isExternal = doc.external_upload === true;
            const isFinal = isExternal
              ? ['signed', 'approved', 'sent'].includes(doc.status)
              : isSignable
                ? doc.status === 'signed'
                : doc.doc_type === 'rom' && ['approved', 'sent'].includes(doc.status);
            const isVoided = doc.status === 'voided';
            const statusBadge = isVoided
              ? { text: 'CANCELLED', cls: 'bg-red-600 text-white' }
              : isFinal
              ? (isExternal
                  ? { text: doc.court_shared_at ? '✓ Final — shared with court' : '✓ Final (uploaded)', cls: 'bg-green-100 text-green-800' }
                  : isSignable
                  ? { text: '✓ Fully signed', cls: 'bg-green-100 text-green-800' }
                  : doc.status === 'sent'
                    ? { text: '✓ Shared with court & parties', cls: 'bg-green-100 text-green-800' }
                    : { text: '✓ Final — shared with court', cls: 'bg-green-100 text-green-800' })
              : doc.status === 'sent'
                ? { text: 'Sent — awaiting signatures', cls: 'bg-amber-100 text-amber-800' }
                : { text: doc.status, cls: 'bg-surface-muted text-gray-600' };
            return (
              <li
                key={doc.id}
                className={`rounded-md border p-2 text-sm ${isVoided ? 'border-red-300 bg-red-50/60' : isFinal ? 'border-green-300 bg-green-50' : 'border-surface-border'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{docTypeLabel(doc.doc_type)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadge.cls}`}>{statusBadge.text}</span>
                </div>
                <div className="mt-0.5 text-xs text-gray-400">
                  {formatDateTime(doc.created_at)}
                  {isExternal && ` · uploaded PDF${doc.original_filename ? ` (${doc.original_filename})` : ''}`}
                </div>
                {isVoided && (
                  <div className="mt-1 text-xs font-medium text-red-700">
                    Cancelled{doc.voided_at ? ` ${formatDateTime(doc.voided_at)}` : ''}
                    {doc.voided_reason ? ` — ${doc.voided_reason}` : ''}
                  </div>
                )}
                {(isExternal || (doc.doc_type === 'stipulation' && doc.status === 'signed')) && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                    {doc.court_shared ? (
                      <>
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">✓ Shared with court</span>
                        {canMutate && (
                          <button
                            type="button"
                            className="text-[11px] text-amber-700 hover:underline"
                            onClick={async () => {
                              if (!(await confirmDialog('Cancel the court share? The clerk immediately loses sight of this document. You can re-share any time.', { danger: true, confirmLabel: 'Cancel share' }))) return;
                              try {
                                await unshareDocumentWithCourt(token, doc.id);
                                setDocuments((prev) => (prev ?? []).map((d) => (d.id === doc.id ? { ...d, court_shared: false, court_share_cancelled: true } : d)));
                              } catch (err) {
                                if (err instanceof EngineError && err.status === 401) return onUnauthorized();
                                setError(err instanceof EngineError ? err.message : 'Could not cancel the share.');
                              }
                            }}
                          >
                            Cancel share with court…
                          </button>
                        )}
                      </>
                    ) : canMutate ? (
                      <>
                        {doc.court_share_cancelled && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">Share cancelled — hidden from the court</span>
                        )}
                        <button
                          type="button"
                          className="btn-primary !py-1 !text-xs"
                          disabled={sharing === doc.id}
                          onClick={() => void shareWithCourt(doc.id)}
                        >
                          {sharing === doc.id ? 'Sharing…' : 'Share with court'}
                        </button>
                      </>
                    ) : (
                      <span className="text-gray-500">Not yet shared with the court</span>
                    )}
                  </div>
                )}
                {canMutate && doc.doc_type === 'rom' && ['draft', 'review'].includes(doc.status) && romEditing !== doc.id && (
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2">
                    <div className="text-xs font-medium text-amber-800">
                      ⚠ This report needs your review before it can be sent to the parties.
                    </div>
                    <button
                      type="button"
                      className="btn-primary mt-1.5 w-full !text-xs"
                      onClick={() => void openRomEditor(doc.id)}
                    >
                      Review &amp; approve report
                    </button>
                  </div>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                  {!isExternal && <button
                    type="button"
                    className="font-medium text-brand hover:underline"
                    onClick={() => void viewDoc(doc.id)}
                  >
                    View document
                  </button>}
                  {/* The PDF endpoint self-heals a missing stored copy, so
                      the button never hides (a signed stipulation with a
                      failed render looked invisible — found live). */}
                  <button
                    type="button"
                    className="font-medium text-brand hover:underline"
                    onClick={() => void openPdf(doc.id)}
                  >
                    Open PDF
                  </button>
                  {canMutate && doc.doc_type === 'rom' && doc.status === 'approved' && (
                    <button
                      type="button"
                      className="font-medium text-brand hover:underline"
                      onClick={() => void openRomEditor(doc.id)}
                    >
                      Review &amp; edit…
                    </button>
                  )}
                  {canMutate && ['stipulation', 'resolution_agreement', 'certificate_of_attempts'].includes(doc.doc_type) && !isExternal && onOpenStipEditor && (
                    <button
                      type="button"
                      className="font-medium text-brand hover:underline"
                      onClick={() => onOpenStipEditor(doc.id)}
                    >
                      Open full editor…
                    </button>
                  )}
                  {canMutate && doc.doc_type === 'stipulation' && ['draft', 'review'].includes(doc.status) && !onOpenStipEditor && (
                    <button
                      type="button"
                      className="font-medium text-brand hover:underline"
                      onClick={() => void openStipEditor(doc.id)}
                    >
                      Edit terms…
                    </button>
                  )}
                  {canMutate && isSignable && doc.status === 'sent' && (
                    <button
                      type="button"
                      className="text-amber-700 hover:underline"
                      onClick={async () => {
                        if (!(await confirmDialog('Cancel all outstanding signing links and pull this document back for edits? Anyone who already signed will need to re-sign the edited version.', { danger: true, confirmLabel: 'Cancel signing' }))) return;
                        try {
                          const res = await cancelSignRequests(token, doc.id);
                          setDocuments((prev) => (prev ?? []).map((d) => (d.id === doc.id ? { ...d, status: 'draft' } : d)));
                          setSigning((prev) => { const next = { ...prev }; delete next[doc.id]; return next; });
                          if (res.signatures_reset > 0) window.alert(`${res.signatures_reset} collected signature(s) were reset — signers must re-sign after your edits.`);
                        } catch (err) {
                          if (err instanceof EngineError && err.status === 401) return onUnauthorized();
                          setError(err instanceof EngineError ? err.message : 'Could not cancel signing.');
                        }
                      }}
                    >
                      Cancel signing &amp; edit…
                    </button>
                  )}
                  {canMutate && isSignable && doc.status === 'signed' && !isExternal && (
                    <button
                      type="button"
                      className="text-amber-700 hover:underline"
                      onClick={async () => {
                        if (!(await confirmDialog('This document is FULLY SIGNED. Void ALL collected signatures and reopen it for edits? Every party will need to re-sign the edited version.', { danger: true, confirmLabel: 'Void signatures' }))) return;
                        if (!(await confirmDialog('Confirm once more: the collected signatures will be voided (with an audit trail). The document itself is kept and becomes editable.', { danger: true, confirmLabel: 'Void signatures' }))) return;
                        try {
                          const res = await cancelSignRequests(token, doc.id, { reopenSigned: true });
                          setDocuments((prev) => (prev ?? []).map((d) => (d.id === doc.id ? { ...d, status: 'draft' } : d)));
                          setSigning((prev) => { const next = { ...prev }; delete next[doc.id]; return next; });
                          window.alert(`${res.signatures_reset} signature(s) voided — edit the document, then send fresh signing links.`);
                        } catch (err) {
                          if (err instanceof EngineError && err.status === 401) return onUnauthorized();
                          setError(err instanceof EngineError ? err.message : 'Could not reopen the document.');
                        }
                      }}
                    >
                      Void signatures &amp; edit…
                    </button>
                  )}
                  {canMutate && doc.doc_type === 'stipulation' && doc.status !== 'voided' && (
                    <button
                      type="button"
                      className="text-red-700 hover:underline"
                      onClick={() => void voidAndRedraft(doc)}
                    >
                      Void &amp; re-draft…
                    </button>
                  )}
                  {canMutate && doc.doc_type === 'rom' && ['approved', 'sent'].includes(doc.status) && (
                    <button
                      type="button"
                      className="font-medium text-green-700 hover:underline"
                      disabled={romBusy}
                      onClick={() => void sendRom(doc.id)}
                    >
                      {doc.status === 'sent' ? 'Resend to parties…' : 'Send to all parties…'}
                    </button>
                  )}
                  {canMutate && isSignable && doc.status !== 'signed' && doc.status !== 'voided' && (
                    <button
                      type="button"
                      className="text-gray-600 hover:text-gray-900"
                      onClick={() => void sendRequests(doc.id)}
                    >
                      Send signing links…
                    </button>
                  )}
                  {isSignable && (
                    <button
                      type="button"
                      className="text-gray-600 hover:text-gray-900"
                      onClick={() => void checkSigning(doc.id)}
                    >
                      {status ? 'Refresh signing status' : 'Check signing status'}
                    </button>
                  )}
                  {canMutate && isSignable && !isVoided && ['draft', 'review'].includes(doc.status) && docCancelling !== doc.id && (
                    <button
                      type="button"
                      className="text-red-700 hover:underline"
                      onClick={() => setDocCancelling(doc.id)}
                    >
                      Cancel draft…
                    </button>
                  )}
                  {canMutate && isCertificate && !isVoided && coaCancelling !== doc.id && (
                    <button
                      type="button"
                      className="text-red-700 hover:underline"
                      onClick={() => { setCoaCancelling(doc.id); setCoaReason('engaged'); setCoaOtherText(''); }}
                    >
                      Cancel COA…
                    </button>
                  )}
                </div>
                {docCancelling === doc.id && !isVoided && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs">
                    <div className="font-medium text-red-800">
                      Cancel this draft {docTypeLabel(doc.doc_type)}?
                    </div>
                    <div className="mt-0.5 text-[11px] text-red-700">
                      It is marked CANCELLED and can no longer be sent or signed. Drafting again starts a
                      fresh document.
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setDocCancelling(null)}>
                        Keep draft
                      </button>
                      <button
                        type="button"
                        className="btn-primary !bg-red-600 !py-1 !text-xs hover:!bg-red-700"
                        onClick={async () => {
                          try {
                            await voidDocument(token, doc.id);
                            setDocuments((prev) => (prev ?? []).map((d) => (d.id === doc.id ? { ...d, status: 'voided', voided_at: new Date().toISOString() } : d)));
                            setDocCancelling(null);
                          } catch (err) {
                            if (err instanceof EngineError && err.status === 401) return onUnauthorized();
                            setError(err instanceof EngineError ? err.message : 'Could not cancel the draft.');
                          }
                        }}
                      >
                        Yes — cancel it
                      </button>
                    </div>
                  </div>
                )}
                {coaCancelling === doc.id && !isVoided && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs">
                    <div className="font-medium text-red-800">Cancel this Certificate of Attempts?</div>
                    <div className="mt-0.5 text-[11px] text-red-700">
                      The clerk keeps seeing it, marked CANCELLED with your reason.
                    </div>
                    <label className="mt-2 flex items-center gap-1.5 text-gray-700">
                      <input type="radio" name={`coa-reason-${doc.id}`} checked={coaReason === 'engaged'} onChange={() => setCoaReason('engaged')} />
                      Party engaged — mediation will proceed
                    </label>
                    <label className="mt-1 flex items-center gap-1.5 text-gray-700">
                      <input type="radio" name={`coa-reason-${doc.id}`} checked={coaReason === 'other'} onChange={() => setCoaReason('other')} />
                      Other:
                      <input
                        className="min-w-0 flex-1 rounded border border-surface-border px-1.5 py-0.5"
                        placeholder="reason the court will see"
                        value={coaOtherText}
                        onFocus={() => setCoaReason('other')}
                        onChange={(e) => setCoaOtherText(e.target.value)}
                      />
                    </label>
                    <div className="mt-2 flex gap-2">
                      <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setCoaCancelling(null)}>
                        Keep certificate
                      </button>
                      <button
                        type="button"
                        className="btn-primary !bg-red-600 !py-1 !text-xs hover:!bg-red-700"
                        disabled={coaReason === 'other' && !coaOtherText.trim()}
                        onClick={async () => {
                          const reason = coaReason === 'engaged' ? 'Party engaged — mediation will proceed' : coaOtherText.trim();
                          try {
                            await voidDocument(token, doc.id, reason);
                            setDocuments((prev) => (prev ?? []).map((d) => (d.id === doc.id ? { ...d, status: 'voided', voided_reason: reason, voided_at: new Date().toISOString() } : d)));
                            setCoaCancelling(null);
                          } catch (err) {
                            if (err instanceof EngineError && err.status === 401) return onUnauthorized();
                            setError(err instanceof EngineError ? err.message : 'Could not cancel the certificate.');
                          }
                        }}
                      >
                        Cancel COA
                      </button>
                    </div>
                  </div>
                )}
                {stipEditing === doc.id && (
                  <div className="mt-2 space-y-1.5 rounded-md bg-surface-muted p-2 text-xs">
                    <div className="font-medium text-gray-600">Agreed terms (one per line, **bold** prints bold)</div>
                    <textarea
                      className="w-full resize-y rounded border border-surface-border px-2 py-1.5 font-mono text-[13px] leading-relaxed"
                      rows={16}
                      value={stipTerms}
                      onChange={(e) => setStipTerms(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary !py-1 !text-xs" disabled={stipBusy} onClick={() => void saveStip(doc.id)}>
                        {stipBusy ? 'Saving…' : 'Save terms'}
                      </button>
                      <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => setStipEditing(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {romEditing === doc.id && (
                  <div className="mt-2 space-y-1.5 rounded-md bg-surface-muted p-2 text-xs">
                    <div className="font-medium text-gray-600">Participants (as printed on the report)</div>
                    {romRows.map((row, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <input
                          className="w-1/2 rounded border border-surface-border px-1.5 py-1"
                          placeholder="Name"
                          value={row.name}
                          onChange={(e) =>
                            setRomRows((rows) => rows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))
                          }
                        />
                        <input
                          className="w-1/2 rounded border border-surface-border px-1.5 py-1"
                          placeholder="Role (e.g. Defendant's Attorney)"
                          value={row.role_label}
                          onChange={(e) =>
                            setRomRows((rows) => rows.map((r, j) => (j === i ? { ...r, role_label: e.target.value } : r)))
                          }
                        />
                        <button
                          type="button"
                          className="shrink-0 text-gray-400 hover:text-red-600"
                          title="Remove"
                          onClick={() => setRomRows((rows) => rows.filter((_, j) => j !== i))}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-brand hover:underline"
                      onClick={() => setRomRows((rows) => [...rows, { name: '', role_label: '' }])}
                    >
                      + Add participant
                    </button>
                    <div className="mt-2 font-medium text-gray-600">Report text (**bold** prints bold)</div>
                    <textarea
                      className="w-full resize-y rounded border border-surface-border px-2 py-1.5 text-[13px] leading-relaxed"
                      rows={16}
                      value={romReportText}
                      onChange={(e) => setRomReportText(e.target.value)}
                    />
                    <div className="font-medium text-gray-600">Notes (only printed if filled in)</div>
                    <textarea
                      className="w-full resize-y rounded border border-surface-border px-2 py-1.5 text-[13px] leading-relaxed"
                      rows={6}
                      value={romNotes}
                      onChange={(e) => setRomNotes(e.target.value)}
                    />
                    <div className="flex items-center gap-2 pt-0.5">
                      <button type="button" className="btn-primary !py-1 !text-xs" disabled={romBusy} onClick={() => void saveRom(doc.id, true)}>
                        {romBusy ? 'Saving…' : 'Save & approve'}
                      </button>
                      <button type="button" className="btn-secondary !py-1 !text-xs" disabled={romBusy} onClick={() => void saveRom(doc.id, false)}>
                        Save only
                      </button>
                      <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => setRomEditing(null)}>
                        Close
                      </button>
                      {romSavedAt === doc.id && <span className="text-green-700">Saved ✓</span>}
                    </div>
                  </div>
                )}
                {status && (
                  <div className="mt-1.5 space-y-1 border-t border-surface-border pt-1.5 text-xs">
                    {/* Signature rows exist from the moment a doc is
                        generated; before signing links go out, "pending"
                        reads as "we sent this" (Danny, 2026-09-11: a
                        fresh draft looked like it was already sent to the
                        parties). Until the doc is sent, say so plainly. */}
                    {doc.status !== 'sent' && doc.status !== 'signed' ? (
                      <div className="space-y-1">
                        <div className="text-gray-500">
                          Signing links not sent yet — parties have NOT received this document.
                        </div>
                        {status.signers.filter((s) => s.sign_url).map((s) => (
                          <div key={s.id} className="flex items-center justify-between gap-2">
                            <span className="text-gray-500">{s.signer_name}</span>
                            <button
                              type="button"
                              className="text-brand hover:underline"
                              title="Copy this signer's private signing link — hand it over by any channel if sends aren't reaching them"
                              onClick={() => void copySignLink(s.id, s.sign_url!)}
                            >
                              {copiedSig === s.id ? 'Copied ✓' : 'Copy link'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div className={status.all_signed ? 'font-medium text-green-700' : 'text-gray-600'}>
                          {status.signed}/{status.total} signed
                          {status.declined > 0 ? ` · ${status.declined} declined` : ''}
                        </div>
                        {status.signers.map((s) => (
                          <div key={s.id} className="flex items-center justify-between gap-2">
                            <span>
                              {s.signer_name}
                              {s.role_label ? ` (${s.role_label})` : ''}
                            </span>
                            <span className="flex items-center gap-2">
                              {s.status === 'pending' && s.sign_url && (
                                <button
                                  type="button"
                                  className="text-brand hover:underline"
                                  title="Copy this signer's private signing link — hand it over by any channel if sends aren't reaching them"
                                  onClick={() => void copySignLink(s.id, s.sign_url!)}
                                >
                                  {copiedSig === s.id ? 'Copied ✓' : 'Copy link'}
                                </button>
                              )}
                              <span
                                className={
                                  s.status === 'signed'
                                    ? 'text-green-700'
                                    : s.status === 'declined'
                                      ? 'text-red-700'
                                      : 'text-gray-500'
                                }
                              >
                                {s.status === 'pending' ? 'awaiting signature' : s.status}
                              </span>
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
